/**
 * Admin API 请求模块
 * 专门用于后台管理系统的 API 调用
 */

import axios from 'axios'
import { ElMessage } from 'element-plus'

const normalizeToken = (token) => {
    if (!token) return ''
    return String(token).replace(/^Bearer\s+/i, '').trim()
}

// 未登录统一跳转到国内登录页
const redirectToLogin = () => {
    const isUsHost = window.location.hostname.includes('admin-us.lingxidance.com')
    if (isUsHost) {
        window.location.href = 'https://cms.luminiea.com/login'
    } else {
        window.location.href = '/login'
    }
}

// 配置 axios
const isDev = process.env.NODE_ENV === 'development'
const baseURL = isDev ? '' : `${window.location.protocol}//${window.location.hostname}`

const adminRequest = axios.create({
    baseURL: baseURL,
    timeout: 60000
})

// Token 刷新锁
let isRefreshing = false
let refreshPromise = null
let refreshRetryCount = 0
const MAX_REFRESH_RETRY = 3

// 请求拦截器
adminRequest.interceptors.request.use(
    config => {
        const rawToken = localStorage.getItem('admin_token')
        const token = normalizeToken(rawToken)
        if (rawToken && token !== rawToken) {
            localStorage.setItem('admin_token', token)
        }
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }
        
        // 环境隔离：业务区域由域名决定，不再允许用户手动切换
        const isUsHost = window.location.hostname.includes('admin-us.lingxidance.com')
        config.headers['X-Business-Region'] = isUsHost ? 'usa' : 'cn'
        
        // 设置 Content-Type
        if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json'
        }
        
        return config
    },
    error => Promise.reject(error)
)

// 处理 Token 过期
async function handleTokenExpired(config) {
    if (refreshRetryCount >= MAX_REFRESH_RETRY) {
        clearAdminAuth()
        redirectToLogin()
        return Promise.reject(new Error('登录已过期，请重新登录'))
    }
    
    if (!isRefreshing) {
        isRefreshing = true
        const refreshToken = localStorage.getItem('admin_refresh_token')
        
        if (refreshToken) {
            refreshPromise = adminRequest.post('/admin/auth/refresh-token', { refresh_token: refreshToken })
                .then(res => {
                    if (res.code === 200) {
                        localStorage.setItem('admin_token', normalizeToken(res.data.token))
                        localStorage.setItem('admin_refresh_token', res.data.refresh_token)
                        isRefreshing = false
                        refreshRetryCount = 0
                        return Promise.resolve(res)
                    } else {
                        throw new Error('Token 刷新失败')
                    }
                })
                .catch(err => {
                    refreshRetryCount++
                    if (refreshRetryCount >= MAX_REFRESH_RETRY) {
                        clearAdminAuth()
                        redirectToLogin()
                    }
                    throw err
                })
                .finally(() => { isRefreshing = false })
        } else {
            clearAdminAuth()
            redirectToLogin()
            return Promise.reject(new Error('未登录'))
        }
    } else {
        refreshRetryCount++
    }
    
    await refreshPromise
    config.headers['Authorization'] = `Bearer ${localStorage.getItem('admin_token')}`
    return adminRequest(config)
}

// 清除管理员认证信息
function clearAdminAuth() {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_refresh_token')
    localStorage.removeItem('admin_user')
    localStorage.removeItem('admin_permissions')
    localStorage.removeItem('admin_menus')
}

// 响应拦截器
adminRequest.interceptors.response.use(
    async response => {
        // axios 的 response.data 就是后端返回的 JSON
        const result = response.data
        
        // 如果后端返回的是字符串，尝试解析
        if (typeof result === 'string') {
            try {
                const parsed = JSON.parse(result)
                return parsed
            } catch (e) {
                console.error('响应解析失败:', e, result)
                return Promise.reject({ code: 500, message: '响应格式错误', data: null })
            }
        }
        
        const { code, message } = result || {}
        const requestUrl = response.config?.url || ''
        
        // 登录接口的 401 不应该走 token 过期逻辑，直接返回错误
        const isLoginRequest = requestUrl.includes('/auth/login')
        
        if (code === 401 && !isLoginRequest) {
            return handleTokenExpired(response.config)
        }
        
        if (code === 403) {
            ElMessage.error(message || '无权限访问')
            return Promise.reject(result)
        }
        
        // 返回完整响应数据
        return result
    },
    async error => {
        // axios 0.21 使用 CancelToken 取消；0.22+ 才有 AbortSignal
        if (axios.isCancel(error)) {
            return Promise.reject({ code: 'CANCELED', message: '请求已取消', data: null })
        }
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            return Promise.reject({ code: 'CANCELED', message: '请求已取消', data: null })
        }
        if (error.response) {
            const { status, data } = error.response
            const requestUrl = error.config?.url || ''
            
            // 登录接口的 401 不应该走 token 过期逻辑，直接返回错误
            const isLoginRequest = requestUrl.includes('/auth/login')
            
            if (status === 401 && !isLoginRequest) {
                return handleTokenExpired(error.config)
            }
            
            if (status === 403) {
                ElMessage.error(data?.message || '无权限访问')
            }
            
            return Promise.reject({
                code: status,
                message: data?.message || '请求失败',
                data: null
            })
        } else if (error.request) {
            return Promise.reject({
                code: 0,
                message: '网络连接失败，请检查网络设置',
                data: null
            })
        } else {
            return Promise.reject({
                code: 0,
                message: error.message || '请求配置错误',
                data: null
            })
        }
    }
)

// ========== Admin Auth API ==========

export const adminAuthApi = {
    // 登录
    login(data) {
        return adminRequest.post('/admin/auth/login', data)
    },
    
    // 登出
    logout() {
        return adminRequest.post('/admin/auth/logout')
    },
    
    // 刷新 Token
    refreshToken(data) {
        return adminRequest.post('/admin/auth/refresh-token', data)
    },
    
    // 获取当前用户信息
    getProfile() {
        return adminRequest.get('/admin/auth/profile')
    },
    
    // 修改密码
    changePassword(data) {
        return adminRequest.post('/admin/auth/change-password', data)
    }
}

// ========== Admin User API ==========

export const adminUserApi = {
    // 获取管理员列表
    getList(params) {
        return adminRequest.get('/admin/admins', { params })
    },
    
    // 获取管理员详情
    getById(id) {
        return adminRequest.get(`/admin/admins/${id}`)
    },
    
    // 创建管理员
    create(data) {
        return adminRequest.post('/admin/admins', data)
    },
    
    // 更新管理员
    update(id, data) {
        return adminRequest.post(`/admin/admins/${id}`, data)
    },
    
    // 删除管理员
    delete(id) {
        return adminRequest.delete(`/admin/admins/${id}`)
    },
    
    // 重置密码
    resetPassword(id, data) {
        return adminRequest.post(`/admin/admins/${id}/reset-password`, data)
    },
    
    // 获取角色列表（用于下拉选择）
    getRoles() {
        return adminRequest.get('/admin/admin-roles')
    }
}

// ========== Admin Role API ==========

export const adminRoleApi = {
    // 获取角色列表
    getList() {
        return adminRequest.get('/admin/admin-roles')
    },
    
    // 获取角色详情
    getById(id) {
        return adminRequest.get(`/admin/admin-roles/${id}`)
    },
    
    // 创建角色
    create(data) {
        return adminRequest.post('/admin/admin-roles', data)
    },
    
    // 更新角色
    update(id, data) {
        return adminRequest.post(`/admin/admin-roles/${id}`, data)
    },
    
    // 删除角色
    delete(id) {
        return adminRequest.delete(`/admin/admin-roles/${id}`)
    },
    
    // 分配权限
    assignPermissions(id, data) {
        return adminRequest.post(`/admin/admin-roles/${id}/permissions`, data)
    },
    
    // 获取权限树
    getPermissions() {
        return adminRequest.get('/admin/permissions/tree')
    }
}

// ========== Audit Log API ==========

export const auditLogApi = {
    // 获取审计日志列表
    getList(params) {
        return adminRequest.get('/admin/audit-logs', { params })
    },
    
    // 获取审计日志详情
    getById(id) {
        return adminRequest.get(`/admin/audit-logs/${id}`)
    },
    
    // 导出审计日志
    export(params) {
        return adminRequest.get('/admin/audit-logs/export', { params })
    },
    
    // 获取筛选选项
    getFilterOptions() {
        return adminRequest.get('/admin/audit-logs/filter-options')
    },
    
    // 获取登录日志
    getLoginLogs(params) {
        return adminRequest.get('/admin/audit-logs/login-logs', { params })
    }
}

// ========== Dashboard API ==========

export const dashboardApi = {
    // 获取统计概览
    getStat() {
        return adminRequest.get('/admin/dashboard/stat')
    },
    
    // 固件统计
    getFirmwareStat() {
        return adminRequest.get('/admin/dashboard/firmware-stat')
    },
    
    // 设备统计
    getDeviceStat() {
        return adminRequest.get('/admin/dashboard/device-stat')
    },
    
    // 用户增长趋势
    getUserTrend(params) {
        return adminRequest.get('/admin/dashboard/user-trend', { params })
    },
    
    // 订单趋势
    getOrderTrend(params) {
        return adminRequest.get('/admin/dashboard/order-trend', { params })
    }
}

// ========== Business User API ==========

export const userManageApi = {
    // 获取用户列表
    getList(params) {
        return adminRequest.get('/admin/users', { params })
    },
    
    // 获取用户详情
    getById(id) {
        return adminRequest.get(`/admin/users/${id}`)
    },
    
    // 创建用户
    create(data) {
        return adminRequest.post('/admin/users', data)
    },
    
    // 更新用户
    update(userUid, data) {
        return adminRequest.put(`/admin/users/${userUid}`, data)
    },
    
    // 删除用户
    delete(userUid) {
        return adminRequest.delete(`/admin/users/${userUid}`)
    },
    
    // 更新用户状态
    updateStatus(id, data) {
        return adminRequest.put(`/admin/users/${id}/status`, data)
    },
    
    // 切换白名单
    toggleWhitelist(userUid) {
        return adminRequest.post('/admin/users/toggle-whitelist', { user_uid: userUid })
    },
    
    // 发送邮箱验证码
    sendEmailCode(email) {
        return adminRequest.post('/admin/users/send-email-code', { email })
    },
    
    // 发送手机验证码
    sendPhoneCode(phone) {
        return adminRequest.post('/admin/users/send-phone-code', { phone })
    },
    
    // 绑定邮箱
    bindEmail(userUid, data) {
        return adminRequest.post(`/admin/users/${userUid}/bind-email`, data)
    },
    
    // 绑定手机
    bindPhone(userUid, data) {
        return adminRequest.post(`/admin/users/${userUid}/bind-phone`, data)
    },
    
    // 通过邮箱重置密码
    resetPasswordByEmail(data) {
        return adminRequest.post('/admin/users/reset-password-email', data)
    },
    
    // 通过手机重置密码
    resetPasswordByPhone(data) {
        return adminRequest.post('/admin/users/reset-password-phone', data)
    },
    
    // 获取用户统计
    getStats() {
        return adminRequest.get('/admin/users/stats')
    },
    
    // 获取用户记忆列表
    getMemoryList(userId) {
        return adminRequest.get('/admin/memory/list', { params: { user_id: userId } })
    },
    
    // 删除用户记忆
    deleteMemory(data) {
        return adminRequest.post('/admin/memory/delete', data)
    }
}

// ========== Business Role API ==========

export const roleManageApi = {
    // 获取角色列表
    getList(params) {
        return adminRequest.get('/admin/roles', { params })
    },
    
    // 获取角色详情
    getById(id) {
        return adminRequest.get(`/admin/roles/${id}`)
    },
    
    // 创建角色
    create(data) {
        return adminRequest.post('/admin/roles', data)
    },
    
    // 更新角色
    update(id, data) {
        return adminRequest.put(`/admin/roles/${id}`, data)
    },
    
    // 删除角色
    delete(id) {
        return adminRequest.delete(`/admin/roles/${id}`)
    },
    
    // 更新优先级
    updatePriority(data) {
        return adminRequest.post('/admin/roles/update-priority', data)
    },
    
    // 获取角色定价
    getPricing(roleUid) {
        return adminRequest.get('/admin/roles/pricing', { params: { role_uid: roleUid } })
    },
    
    // 保存角色定价
    savePricing(data) {
        return adminRequest.post('/admin/roles/pricing', data)
    },
    
    // 删除角色定价
    deletePricing(data) {
        return adminRequest.post('/admin/roles/pricing-delete', data)
    },
    
    // 获取所有角色 UID
    getAllUids() {
        return adminRequest.get('/admin/roles/all-uids')
    }
}

// ========== Role Audio API ==========

export const roleAudioApi = {
    // 获取角色音频列表
    getList(roleUid) {
        return adminRequest.post('/admin/roleAudio/list', { role_uid: roleUid })
    },
    
    // 保存角色音频
    save(data) {
        return adminRequest.post('/admin/roleAudio/save', data)
    },
    
    // 删除角色音频
    delete(id) {
        return adminRequest.post('/admin/roleAudio/delete', { id })
    }
}

// ========== 角色剧情视频 ==========

export const rolePlotVideoApi = {
    getList(roleUid) {
        return adminRequest.post('/admin/rolePlotVideo/list', { role_uid: roleUid })
    },
    create(data) {
        return adminRequest.post('/admin/rolePlotVideo/create', data)
    },
    update(data) {
        return adminRequest.post('/admin/rolePlotVideo/update', data)
    },
    delete(id) {
        return adminRequest.post('/admin/rolePlotVideo/delete', { id })
    }
}

// ========== 互动视频（与角色无关）==========

export const interactiveVideoApi = {
    getList(params) {
        return adminRequest.get('/admin/interactive-videos/list', { params })
    },
    create(data) {
        return adminRequest.post('/admin/interactive-videos/create', data)
    },
    update(data) {
        return adminRequest.post('/admin/interactive-videos/update', data)
    },
    delete(id) {
        return adminRequest.post('/admin/interactive-videos/delete', { id })
    }
}

// ========== Device Channel API ==========

export const deviceChannelApi = {
    // 获取渠道列表
    getList(params) {
        return adminRequest.get('/admin/device-channels', { params })
    },
    
    // 创建渠道
    create(data) {
        return adminRequest.post('/admin/device-channels', data)
    },
    
    // 更新渠道
    update(id, data) {
        return adminRequest.put(`/admin/device-channels/${id}`, data)
    },
    
    // 删除渠道
    delete(id) {
        return adminRequest.delete(`/admin/device-channels/${id}`)
    },
    
    // 批量删除（下线）
    batchDelete(ids) {
        return adminRequest.post('/admin/device-channels/batch-delete', { ids })
    },
    
    // 批量上线
    batchOnline(ids) {
        return adminRequest.post('/admin/device-channels/batch-online', { ids })
    },
    
    // 获取渠道素材列表
    getMaterialList(params) {
        return adminRequest.get('/admin/device-channels/material-list', { params })
    },
    
    // 创建渠道素材
    createMaterial(data) {
        return adminRequest.post('/admin/device-channels/material-create', data)
    },
    
    // 上传渠道素材
    uploadMaterial(formData, config) {
        return adminRequest.post('/admin/upload/channel-material', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            ...config
        })
    },
    
    // 获取渠道礼物列表
    getRoleGiftList(params) {
        return adminRequest.get('/admin/device-channels/role-gift-list', { params })
    },
    
    // 获取所有角色（用于礼物配置）
    getAllRoles() {
        return adminRequest.get('/admin/device-channels/role-gift-all-roles')
    },
    
    // 创建渠道礼物
    createRoleGift(data) {
        return adminRequest.post('/admin/device-channels/role-gift-create', data)
    },
    
    // 更新渠道礼物
    updateRoleGift(id, data) {
        return adminRequest.put(`/admin/device-channels/role-gift/${id}`, data)
    },
    
    // 删除渠道礼物
    deleteRoleGift(id) {
        return adminRequest.delete(`/admin/device-channels/role-gift/${id}`)
    },
    
    // 渠道礼物排序
    sortRoleGift(data) {
        return adminRequest.post('/admin/device-channels/role-gift-sort', data)
    },
    
    // 获取渠道角色定价
    getRolePricing(params) {
        return adminRequest.get('/admin/device-channels/role-pricing', { params })
    },
    
    // 保存渠道角色定价
    saveRolePricing(data) {
        return adminRequest.post('/admin/device-channels/role-pricing', data)
    },
    
    // 更新素材状态
    updateMaterial(id, data) {
        return adminRequest.put(`/admin/device-channels/material/${id}`, data)
    },
    
    // 删除素材
    deleteMaterial(id) {
        return adminRequest.delete(`/admin/device-channels/material/${id}`)
    },

    // 获取渠道功能配置
    getFunctions(params) {
        return adminRequest.get('/admin/device-channels/function-list', { params })
    },

    // 保存渠道功能配置
    saveFunctions(data) {
        return adminRequest.post('/admin/device-channels/function-save', data)
    }
}

// ========== Order API ==========

export const orderManageApi = {
    // 获取订单列表
    getList(params) {
        return adminRequest.get('/admin/orders', { params })
    },
    
    // 获取订单详情
    getById(id) {
        return adminRequest.get(`/admin/orders/${id}`)
    },
    
    // 获取订单统计
    getStatistics(params) {
        return adminRequest.get('/admin/orders/statistics', { params })
    },
    
    // 更新订单状态
    updateStatus(id, data) {
        return adminRequest.put(`/admin/orders/${id}/status`, data)
    },
    
    // 导出订单
    export(params) {
        return adminRequest.get('/admin/orders/export', { params })
    },
    
    // 获取筛选选项
    getFilterOptions() {
        return adminRequest.get('/admin/orders/filter-options')
    }
}

// ========== Device API ==========

export const deviceApi = {
    // 获取设备列表
    getList(params) {
        return adminRequest.get('/admin/device', { params })
    },
    
    // 获取设备详情
    getById(id) {
        return adminRequest.get(`/admin/device/${id}`)
    },
    
    // 创建设备
    create(data) {
        return adminRequest.post('/admin/device', data)
    },
    
    // 更新设备
    update(id, data) {
        return adminRequest.put(`/admin/device/${id}`, data)
    },
    
    // 删除设备
    delete(id) {
        return adminRequest.delete(`/admin/device/${id}`)
    },
    
    // 设备复位（清除与该设备相关的赠送订单和购买记录）
    reset(id) {
        return adminRequest.post(`/admin/device/reset/${id}`)
    },
    
    // 获取渠道选项
    getChannelOptions() {
        return adminRequest.get('/admin/device/channels')
    },
    
    // 批量切换状态
    batchToggleStatus(data) {
        return adminRequest.post('/admin/device/batch-toggle', data)
    }
}

// ========== Firmware API ==========

export const firmwareApi = {
    // 获取固件列表
    getList(params) {
        return adminRequest.get('/admin/firmware', { params })
    },
    
    // 获取固件详情
    getById(id) {
        return adminRequest.get(`/admin/firmware/${id}`)
    },
    
    // 创建固件
    create(data) {
        return adminRequest.post('/admin/firmware', data)
    },
    
    // 更新固件
    update(id, data) {
        return adminRequest.put(`/admin/firmware/${id}`, data)
    },
    
    // 删除固件
    delete(id) {
        return adminRequest.delete(`/admin/firmware/${id}`)
    },
    
    // 发布固件
    publish(id) {
        return adminRequest.post(`/admin/firmware/${id}/publish`)
    },
    
    // 下线固件
    offline(id) {
        return adminRequest.post(`/admin/firmware/${id}/offline`)
    }
}

// ========== Function Group API ==========

export const functionGroupApi = {
    // 获取功能小类列表
    getList(params) {
        return adminRequest.get('/admin/function-group', { params })
    },

    // 获取单个功能小类
    getById(funcGroup) {
        return adminRequest.get(`/admin/function-group/${encodeURIComponent(funcGroup)}`)
    },

    // 创建功能小类
    create(data) {
        return adminRequest.post('/admin/function-group', data)
    },

    // 更新功能小类（func_group 作为主键，不允许修改）
    update(funcGroup, data) {
        return adminRequest.put(`/admin/function-group/${encodeURIComponent(funcGroup)}`, data)
    },

    // 删除功能小类
    delete(funcGroup) {
        return adminRequest.delete(`/admin/function-group/${encodeURIComponent(funcGroup)}`)
    }
}

// ========== Channel API ==========

export const channelApi = {
    // 获取渠道列表
    getList(params) {
        return adminRequest.get('/admin/channels', { params })
    },

    // 渠道下拉选项（支持分页 + 关键字模糊搜索）
    getOptions(params) {
        return adminRequest.get('/admin/channels/options', { params })
    },
    
    // 获取所有渠道
    getAll() {
        return adminRequest.get('/admin/channels/all')
    },
    
    // 获取渠道详情
    getById(id) {
        return adminRequest.get(`/admin/channels/${id}`)
    },
    
    // 创建渠道
    create(data) {
        return adminRequest.post('/admin/channels', data)
    },
    
    // 更新渠道
    update(id, data) {
        return adminRequest.put(`/admin/channels/${id}`, data)
    },
    
    // 删除渠道
    delete(id) {
        return adminRequest.delete(`/admin/channels/${id}`)
    }
}

// ========== Voice API ==========

export const voiceApi = {
    // 获取音色列表
    getList(params) {
        return adminRequest.get('/admin/voice', { params })
    },
    
    // 获取所有音色
    getAll() {
        return adminRequest.get('/admin/voice/all')
    },
    
    // 获取音色详情
    getById(id) {
        return adminRequest.get(`/admin/voice/${id}`)
    },
    
    // 创建音色
    create(data) {
        return adminRequest.post('/admin/voice', data)
    },
    
    // 更新音色
    update(id, data) {
        return adminRequest.put(`/admin/voice/${id}`, data)
    },
    
    // 删除音色
    delete(id) {
        return adminRequest.delete(`/admin/voice/${id}`)
    },
    
    // 复刻音色
    clone(id) {
        return adminRequest.post(`/admin/voice/${id}/clone`)
    }
}

// ========== 音色设计（DashScope 声音设计）==========
export const voiceDesignApi = {
    list(params) {
        return adminRequest.get('/admin/voice-design/list', { params })
    },
    query(params) {
        return adminRequest.get('/admin/voice-design/query', { params })
    },
    create(data) {
        return adminRequest.post('/admin/voice-design/create', data, { timeout: 120000 })
    },
    updateLocal(id, data) {
        return adminRequest.post(`/admin/voice-design/update/${id}`, data)
    },
    deleteRemote(data) {
        return adminRequest.post('/admin/voice-design/delete', data)
    },
    byteplusCatalog() {
        return adminRequest.get('/admin/voice-design/byteplus-catalog')
    },
    createByteplus(data) {
        return adminRequest.post('/admin/voice-design/create-byteplus', data, { timeout: 180000 })
    }
}

// ========== Audio API ==========

export const audioApi = {
    // 获取音频列表
    getList(params) {
        return adminRequest.get('/admin/audios', { params })
    },
    
    // 获取音频详情
    getById(id) {
        return adminRequest.get(`/admin/audios/${id}`)
    },
    
    // 批量获取音频
    getBatch(ids) {
        return adminRequest.post('/admin/audios/batch', { ids })
    },
    
    // 创建音频
    create(data) {
        return adminRequest.post('/admin/audios', data)
    },
    
    // 更新音频
    update(id, data) {
        return adminRequest.put(`/admin/audios/${id}`, data)
    },
    
    // 删除音频
    delete(id) {
        return adminRequest.delete(`/admin/audios/${id}`)
    },
    
    // 获取音频分类
    getCategories() {
        return adminRequest.get('/admin/audios/categories')
    },
    
    // 获取呼吸音频列表
    getBreathList(params) {
        return adminRequest.get('/admin/audios/breath', { params })
    }
}

// ========== Flirt API ==========

export const flirtApi = {
    // 获取意图列表
    getList(params) {
        return adminRequest.get('/admin/flirt', { params })
    },
    
    // 获取所有意图
    getAll() {
        return adminRequest.get('/admin/flirt/all')
    },
    
    // 获取意图详情
    getById(id) {
        return adminRequest.get(`/admin/flirt/${id}`)
    },
    
    // 创建意图
    create(data) {
        return adminRequest.post('/admin/flirt', data)
    },
    
    // 更新意图
    update(id, data) {
        return adminRequest.put(`/admin/flirt/${id}`, data)
    },
    
    // 删除意图
    delete(id) {
        return adminRequest.delete(`/admin/flirt/${id}`)
    },
    
    // 批量删除
    batchDelete(ids) {
        return adminRequest.post('/admin/flirt/batch-delete', { ids })
    },
    
    // 获取意图分类选项
    getOptions() {
        return adminRequest.get('/admin/flirt/options')
    },
    
    // 获取意图分类
    getCategories() {
        return adminRequest.get('/admin/flirt/categories')
    }
}

// ========== Upload API ==========

export const uploadApi = {
    // 通用上传
    upload(file, type = 'general') {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)
        return adminRequest.post('/admin/upload/file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    // 上传图片
    uploadImage(file) {
        const formData = new FormData()
        formData.append('file', file)
        return adminRequest.post('/admin/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    // 上传图标
    uploadIcon(file) {
        const formData = new FormData()
        formData.append('file', file)
        return adminRequest.post('/admin/upload/icon', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    // 上传音频
    uploadAudio(file) {
        const formData = new FormData()
        formData.append('file', file)
        return adminRequest.post('/admin/upload/audio', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    // 上传视频（可选传入 axios 配置，如 onUploadProgress）
    uploadVideo(file, config = {}) {
        const formData = new FormData()
        formData.append('file', file)
        const { onUploadProgress, ...rest } = config
        return adminRequest.post('/admin/upload/video', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
            ...rest
        })
    },
    
    // 上传固件
    uploadFirmware(file) {
        const formData = new FormData()
        formData.append('file', file)
        return adminRequest.post('/admin/upload/firmware', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },

    // 上传小说文本（人物蒸馏）
    uploadNovelText(file) {
        const formData = new FormData()
        formData.append('file', file)
        return adminRequest.post('/admin/upload/novel-text', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    // 删除文件
    delete(url) {
        return adminRequest.post('/admin/upload/delete', { url })
    }
}

// ========== Task API ==========
export const taskApi = {
    // 获取任务列表
    getList(params) {
        return adminRequest.get('/admin/tasks', { params })
    },

    // 创建任务
    create(data) {
        return adminRequest.post('/admin/tasks/create', data)
    },

    // 获取任务详情
    getById(id) {
        return adminRequest.get(`/admin/tasks/${id}`)
    },

    // 通过 UID 获取任务
    getByUid(taskUid) {
        return adminRequest.get('/admin/tasks/get-by-uid', { params: { task_uid: taskUid } })
    },

    // 取消任务
    cancel(data) {
        return adminRequest.post('/admin/tasks/cancel', data)
    },

    // 重试任务
    retry(data) {
        return adminRequest.post('/admin/tasks/retry', data)
    },

    // 获取 worker 列表
    getWorkers() {
        return adminRequest.get('/admin/tasks/workers')
    },

    // 控制 worker 运行状态
    controlWorker(data) {
        return adminRequest.post('/admin/tasks/worker-control', data)
    },

    // 手动触发过期 processing 恢复
    recoverExpired(data = {}) {
        return adminRequest.post('/admin/tasks/recover-expired', data)
    },

    // 创建人物蒸馏任务
    createCharacterDistillation(data) {
        return adminRequest.post('/admin/tasks/create-character-distillation', data)
    },

    // 创建订单超时任务
    createOrderTimeout(data) {
        return adminRequest.post('/admin/tasks/create-order-timeout', data)
    }
}

/** 提示词一键优化：仅 Redis 临时作业（不写 t_task），由 task-worker 消费队列 */
export const promptOptimizeApi = {
    start(data) {
        return adminRequest.post('/admin/prompt-optimize/start', data)
    },
    getStatus(jobId) {
        return adminRequest.get('/admin/prompt-optimize/status', { params: { job_id: jobId } })
    },
    cancel(data) {
        return adminRequest.post('/admin/prompt-optimize/cancel', data)
    }
}

// ========== Config API ==========
export const adminConfigApi = {
    // 获取业务区域列表
    getBusinessRegions() {
        return adminRequest.get('/admin/config/business-regions')
    }
}

export default adminRequest
